import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { COGNITO_CONFIG } from 'constants/localStorageConstants';
import { findAccountIdFromUrl, getQueryParamsFromLocation } from 'utils/urlHelpers';
import { fetchMeDetails } from 'apis/authentication.api';
import { fetchAccountData, fetchAccounts } from 'apis/accounts.api';
import ErrorBoundary from 'components/elements/ErrorBoundary';
import AlertReload from 'components/elements/AlertReload';
import 'components/App.scss';
import { manageLocalStorageVersion } from 'services/localStorageVersion';
import {
	checkRedirectURIAllowed,
	createAdminPageRedirect
} from 'components/authentication/authenticationUtils';
import { objectHasKeys } from 'utils/helpers';
import { setShowReloadMessage } from 'reducers/globalSlice';
import {
	accountDataLoadingSelector,
	accountDataSelector,
	accountsLoadingSelector,
	accountsPaginationSelector,
	currentAccountIdSelector,
	currentUserSelector,
	refreshTokenSelector,
	setAccountExpiringDialogOpen,
	setAccountsLoading,
	tokenSelector,
	userAccountsSelector
} from 'reducers/userAuthSlice';
import * as Sentry from '@sentry/react';
import { unauthenticatedRoutes } from 'constants/generalConstants';
import { usePrevious } from 'utils/customHooks';
import { useIntercom } from 'react-use-intercom';

const styles = {
	fullWidth: {
		width: '100%'
	}
};

const App = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [sentryConfigured, setSentryConfigured] = useState(false);
	const [intercomConfigured, setIntercomConfigured] = useState(false);
	const token = useSelector(tokenSelector);
	const refreshToken = useSelector(refreshTokenSelector);
	const currentAccountId = useSelector(currentAccountIdSelector);
	const user = useSelector(currentUserSelector);
	const { pathname } = location;
	const isUnauthenticatedRoute = unauthenticatedRoutes.includes(pathname);
	const queryParameters = getQueryParamsFromLocation(location);
	const previousLocation = usePrevious(location);
	const accounts = useSelector(userAccountsSelector);
	const accountsPagination = useSelector(accountsPaginationSelector);
	const accountsLoading = useSelector(accountsLoadingSelector);
	const accountData = useSelector(accountDataSelector);
	const accountDataLoading = useSelector(accountDataLoadingSelector);

	const { update } = useIntercom();

	/**
	 * @description on major or minor version updates
	 */
	const onNewMajorMinorVersion = useCallback(() => {
		// if user is not logged in, refresh page
		if (token) {
			// show toast message to have user log out
			dispatch(setShowReloadMessage({ isOpen: true, versionUpdate: 'minor-major' }));
		} else {
			window.location.reload();
		}
	}, [dispatch, token]);

	/**
	 * @description on patch version updates
	 */
	const onNewPatchVersion = useCallback(() => {
		// if user is not logged in, refresh page
		if (token) {
			// show toast message to have user reload page
			dispatch(setShowReloadMessage({ isOpen: true, versionUpdate: 'patch' }));
		} else {
			window.location.reload();
		}
	}, [dispatch, token]);

	/**
	 * @description if selected Account does match url Account, redirect back to selected Account.
	 * @description edge case: user manually update Account id in url.
	 * @memberof App
	 */
	const urlAccountMatchCheck = useCallback(
		location => {
			const accountId = findAccountIdFromUrl(pathname);
			if (accountId === Number.parseInt(currentAccountId)) return;

			let splitUrl = pathname.split('/');
			splitUrl[2] = currentAccountId;

			let url = splitUrl.reduce((accumulator, item) => {
				if (!item) return accumulator;
				return accumulator.concat(`/${item}`);
			}, '');

			//If navigate directly to /account/id then add airspace
			if (splitUrl.length === 3) {
				url = url.concat('/airspace');
			}
			if (url) return navigate(`${url}${location.search}`);
		},
		[currentAccountId, navigate, pathname]
	);

	/**
	 * @description check on every render
	 * @description redirect user based on login status
	 * @param ( object ) location object from navigate
	 */
	const checkAccountStatus = useCallback(() => {
		const isPostLoginFlow = pathname.includes('post-login-flow');
		if (isPostLoginFlow) {
			//Do nothing, the app is in the correct state
			return;
		}

		if (!isPostLoginFlow) {
			if (pathname.includes('account')) {
				//The user is coming from a link that includes the account number
				return;
			}

			if (!currentAccountId) {
				//If there is no current accountId selected, send user to post-login-flow
				//This will use the token to get the user and their accounts and then redirect to /account-picker
				return navigate('/post-login-flow');
			}

			if (!pathname.includes('account')) {
				//If user is logged in but does not have path navigate. redirect user to airspace.
				return navigate(`/account/${currentAccountId}/airspace`);
			}
		}

		urlAccountMatchCheck(pathname);
	}, [currentAccountId, navigate, urlAccountMatchCheck, pathname]);

	/**
	 * @description check if user has Role and if set
	 * @description set userRole if it's not set
	 * @memberof App
	 */
	const fetchSetUserRole = useCallback(async () => {
		// if false, user is NOT Logged In
		if (!currentAccountId) return;

		const res = await dispatch(fetchMeDetails());

		if (!res) return;

		if (
			queryParameters.success_redirect_uri &&
			checkRedirectURIAllowed(queryParameters.success_redirect_uri)
		) {
			//When coming from admin page and the token is expired, the call to fetchMeDetails will trigger a token refresh
			//Pass new token back to the success_redirect_uri (admin page)
			const cognitoConfig = JSON.parse(localStorage.getItem(COGNITO_CONFIG));
			const redirect = createAdminPageRedirect({
				host: cognitoConfig.host,
				clientId: cognitoConfig.clientId,
				successRedirectURI: queryParameters.success_redirect_uri,
				idToken: token,
				refreshToken
			});
			window.location.assign(redirect);
		}
	}, [currentAccountId, dispatch, queryParameters.success_redirect_uri, token, refreshToken]);

	/**
	 * @description get account settings for features
	 * @memberof App
	 */
	const fetchAccountSettings = useCallback(async () => {
		// if false, user is not logged in, or account dats is loading, or current account id is same as state account id

		if (
			!currentAccountId ||
			accountDataLoading ||
			(accountData && accountData.id === currentAccountId)
		) {
			return;
		}
		const data = await dispatch(fetchAccountData());
		dispatch(setAccountExpiringDialogOpen(data?.expiring_soon));
	}, [accountData, accountDataLoading, currentAccountId, dispatch]);

	const getAccounts = useCallback(
		async page => {
			dispatch(setAccountsLoading(true));
			await dispatch(fetchAccounts(page));
			dispatch(setAccountsLoading(false));
		},
		[dispatch]
	);

	useEffect(() => {
		if (token) {
			checkAccountStatus();
			fetchAccountSettings();
			if (currentAccountId && !accounts && !accountsLoading) {
				// Get first set of accounts, if no accounts found in Redux store
				getAccounts(1);
			} else if (
				currentAccountId &&
				accounts &&
				!accountsLoading &&
				accountsPagination?.current_page !== accountsPagination?.last_page
			) {
				// Incrementally load all pages of accounts
				getAccounts(accountsPagination.current_page + 1);
			}
		} else {
			if (!isUnauthenticatedRoute) {
				//The user is hitting an authenticated route without a token, go to login page
				navigate('/login');
			}
		}
	}, [
		accounts,
		accountsLoading,
		accountsPagination,
		checkAccountStatus,
		currentAccountId,
		fetchAccountSettings,
		fetchSetUserRole,
		getAccounts,
		isUnauthenticatedRoute,
		navigate,
		token
	]);

	useEffect(() => {
		// fetch user only if it doesn't exist in state
		if (token && !user) {
			fetchSetUserRole();
		}
	}, [fetchSetUserRole, token, user]);

	useEffect(() => {
		// fetch user only if navigation changes
		if (token && user && previousLocation?.pathname !== location?.pathname) {
			fetchSetUserRole();
		}
	}, [fetchSetUserRole, location, previousLocation, token, user]);

	useEffect(() => {
		manageLocalStorageVersion({
			onNewMajorVersion: onNewMajorMinorVersion,
			onNewMinorVersion: onNewMajorMinorVersion,
			onNewPatchVersion: onNewPatchVersion
		});
	}, [onNewMajorMinorVersion, onNewPatchVersion]);

	useEffect(() => {
		if (objectHasKeys(user) && !sentryConfigured) {
			// set user info when posting error to Sentry
			Sentry.setUser({
				id: user.id,
				email: user.email
			});
			setSentryConfigured(true);
		}
	}, [sentryConfigured, user]);

	useEffect(() => {
		if (objectHasKeys(user) && objectHasKeys(accountData) && !intercomConfigured) {
			//Send user and account info to Intercom
			update({
				user_id: user.id,
				email: user.email,
				customAttributes: {
					account_id: accountData.id,
					subscription_status: accountData.subscription_status
				}
			});
			setIntercomConfigured(true);
		}
	}, [intercomConfigured, user, accountData, update]);

	useEffect(() => {
		if (objectHasKeys(accountData)) {
			//Update account data in Intercom when user switches accounts
			update({
				customAttributes: {
					account_id: accountData.id,
					subscription_status: accountData.subscription_status
				}
			});
		}
	}, [accountData, update]);

	return (
		<Box className='App'>
			<Box sx={styles.fullWidth}>
				<Outlet />
			</Box>
			<ErrorBoundary>
				<AlertReload />
			</ErrorBoundary>
		</Box>
	);
};

export default App;
